import React from 'react';
import {BaseInputProps} from '../..';

import {Container, Row, Index, Title, Inputs} from './styles';

import Input from '../input';
import {
  AsyncMultiSelect,
  AudioRecorder,
  DatePicker,
  InputButton,
  MultiSelect,
  NumberInput,
  Select,
  Switch,
  TimePicker,
} from '..';
import {InputProps} from '../input';
import {InputButtonProps} from '../input-button';
import TextArea, {TextAreaProps} from '../text-area';
import {useFormContext} from 'react-hook-form';
import {MultiSelectProps} from '../multi-select';
import {SelectProps} from '../select';
import {DatePickerProps} from '../date-picker';
import {TimePickerProps} from '../time-picker';
import {
  TranslationKeyProps,
  translatedProps,
} from 'shared/util/translated-props';
import getNestedKey from 'shared/util/get-nested-key';
import {SwitchProps} from '../switch';
import {NumberInputProps} from '../number-input';
import {AudioRecorderProps} from '../audio-recorder';
import getSentenceGender from 'shared/util/find-sentence-gender';
import {AsyncMultiSelectProps} from '../async-multi-select';

export interface SectionProps<T = any> extends BaseInputProps {
  inputs: (() => BaseInputProps)[] | ((data: T) => (() => BaseInputProps)[]);
  index?: number;
  translationKey: TranslationKeyProps;
}

const Section: React.FC<SectionProps> = ({
  label,
  inputs,
  index,
  translationKey,
}) => {
  const {watch} = useFormContext();

  const handleGetInput = (
    input: BaseInputProps,
    translationKey: TranslationKeyProps,
  ) => {
    if (typeof input.hidden === 'function' && input.hidden(watch()))
      return null;
    if (typeof input.hidden !== 'function' && input.hidden) return null;

    if (!input.label)
      input.label = getNestedKey(
        typeof translationKey !== 'string'
          ? translationKey
          : translatedProps[translationKey as keyof typeof translatedProps],
        input.name!,
      );

    const sentenceGender = getSentenceGender(input.label);

    if (!input.placeholder)
      input.placeholder = `Informe ${sentenceGender} ${input.label.toLocaleLowerCase()}`;

    switch (input.type) {
      case 'input-button':
        return (
          <InputButton key={input.name} {...(input as InputButtonProps)} />
        );
      case 'text-area':
        return <TextArea key={input.name} {...(input as TextAreaProps)} />;
      case 'select':
        return <Select key={input.name} {...(input as SelectProps)} />;
      case 'multi-select':
        return (
          <MultiSelect key={input.name} {...(input as MultiSelectProps)} />
        );
      case 'date-picker':
        return <DatePicker key={input.name} {...(input as DatePickerProps)} />;
      case 'time-picker':
        return <TimePicker key={input.name} {...(input as TimePickerProps)} />;
      case 'switch':
        return <Switch key={input.name} {...(input as SwitchProps)} />;
      case 'number-input':
        return (
          <NumberInput key={input.name} {...(input as NumberInputProps)} />
        );
      case 'audio-recorder':
        return (
          <AudioRecorder key={input.name} {...(input as AudioRecorderProps)} />
        );
      case 'async-multi-select':
        return (
          <AsyncMultiSelect
            key={input.name}
            {...(input as AsyncMultiSelectProps)}
          />
        );
      default:
        return <Input key={input.name} {...(input as InputProps)} />;
    }
  };

  return (
    <Container>
      <Row>
        {index && <Index>{index}</Index>}
        <Title>{label}</Title>
      </Row>
      <Inputs>
        {typeof inputs === 'function'
          ? inputs(watch()).map((getInput) =>
              handleGetInput(getInput(), translationKey),
            )
          : inputs.map((getInput) =>
              handleGetInput(getInput(), translationKey),
            )}
      </Inputs>
    </Container>
  );
};

export default Section;
